// External
import { useState, useMemo, useRef, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

// Components
import { DisplaySelectedButton } from "components/buttons/DisplaySelectedButton";
import { BroadcastDetailsGatingOptions } from "components/forms/BroadcastDetailsForm/BroadcastDetailsGatingOptions";
import { CategoriesAssignment } from "components/inputs/autocomplete/categories-assignment";
import { StreamQualitySelect } from "components/inputs/select/StreamQualitySelect";
import { ScheduleVideoVisibilityBar } from "components/inputs/schedule-video-visibility-bar";
import { TextInput } from "components/inputs/text-input/TextInput";
import { TextArea } from "components/inputs/textarea/Textarea";
import { Toggle } from "components/inputs/toggle/Toggle";
import { DestinationDisplay } from "components/modal/NewLivestreamModal/steps/SelectDestination/destinations/DestinationDisplay";
import { PlayerSelectModal } from "components/modal/PlayerSelectModal";
import { WebLinkSelectModal } from "components/modal/WebLinkSelectModal";
import { ThumbnailUploader } from "components/video-upload/components/thumbnail-uploader";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/reducers";
import {
    updateGeneralForm,
    updateVideoPlayerForm
} from "store/platforms/slice";
import { closeCurrentModal } from "store/modal/slice";

// Hooks and Helpers
import { useClaimCheck } from "hooks/useClaimCheck";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { exists } from "helpers/booleans";

// Types
import { DestinationDescriptor } from "views/page-content/platforms/platform/simulcast/types";
import { PlatformId } from "views/page-content/platforms/types";
import { ValidationErrors } from "../../types";
import { QualitySelection } from "store/platforms/types";
import {
    BroadcastStatus,
    BroadcastWebLink,
    Category,
    CategoryType
} from "@switcherstudio/switcher-api-client";

// Styles and assets
import DEFAULT_THUMBNAIL_URL from "assets/placeholder-multistream.png";
import styles from "./index.module.scss";

export interface LivestreamDetailsStepProps {
    validationErrors: ValidationErrors[];
    setOnAdvanceStep: (callback: () => void) => void;
    SpeedTestDisplay: JSX.Element;
}

export const LivestreamDetailsStep = ({
    validationErrors,
    setOnAdvanceStep,
    SpeedTestDisplay
}: LivestreamDetailsStepProps) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const errorCopy = {
        [ValidationErrors.TitleRequired]: t("errors:name-error"),
        [ValidationErrors.StartTimeMustBeInFuture]: t(
            "errors:start-time-invalid"
        ),
        [ValidationErrors.FacebookDescriptionRequired]: t(
            "errors:description-error"
        )
    };

    const { userInfo } = useSelector((state: RootState) => state.user);

    const {
        general: { generalForm },
        videoplayer: { videoPlayerForm }
    } = useSelector((state: RootState) => state.platforms);

    const [playlistSelectModalIsOpen, setPlaylistSelectModalIsOpen] =
        useState<boolean>(false);
    const [linkSelectModalIsOpen, setLinkSelectModalIsOpen] =
        useState<boolean>(false);
    const [pricingSelectModalOpen, setPricingSelectModalOpen] =
        useState<boolean>(false);
    const hasCatalogClaim = useClaimCheck("catalog");

    const externalDestinations = useMemo<DestinationDescriptor[]>(
        () =>
            generalForm.selectedSimulcastDestinations.filter(
                (d) => d.platform !== PlatformId.VideoPlayer
            ),
        [generalForm.selectedSimulcastDestinations]
    );

    const isShopifyCustomer = useMemo(
        () => userInfo?.Claims?.Details?.FeatureClaims?.includes("shopify"),
        [userInfo]
    );

    // Shopify store credentials
    const { data: shopifyStoreCredentials, loading: storeCredentialsLoading } =
        useSwitcherClient((client) => client.shopify_GetCredentials, {
            requestImmediately: isShopifyCustomer,
            args: []
        });

    useEffect(() => {
        if (!generalForm?.scheduledImageFileUrl) {
            const setDefaultBlob = async () => {
                const res = await fetch(DEFAULT_THUMBNAIL_URL);
                const blob = await res.blob();
                const file = new File([blob], "default.png", {
                    type: blob.type
                });

                if (file) {
                    dispatch(
                        updateGeneralForm({
                            scheduledImageFileUrl: DEFAULT_THUMBNAIL_URL,
                            scheduledImageFile: file,
                            hasCustomThumbnail: false
                        })
                    );
                }
            };

            setDefaultBlob();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validationErrorBox = useRef<HTMLElement | null>(null);

    useEffect(() => {
        setOnAdvanceStep(() => {
            if (validationErrors.length > 0) {
                validationErrorBox.current?.scrollIntoView({
                    behavior: "smooth"
                });

                validationErrorBox.current?.focus();
            }
        });
    }, [validationErrors, setOnAdvanceStep]);

    const emptyState = (
        <Trans
            i18nKey={t("players-select-modal:event-creation-empty")}
            components={{
                link1: (
                    <Link
                        to={"/catalog"}
                        title="Video Catalog"
                        className={styles["link"]}
                        onClick={() => dispatch(closeCurrentModal())}
                    />
                )
            }}
        />
    );

    return (
        <>
            <h6 className={styles["bold-heading"]}>
                {t("platforms:add-details")}
            </h6>
            {validationErrors.length > 0 && (
                <section
                    role="alert"
                    aria-live="assertive"
                    className={styles["error-box"]}
                    id="validation-error-box"
                    ref={validationErrorBox}
                >
                    <ul>
                        {validationErrors.map((e) => {
                            if (!errorCopy[e]) return null;
                            return (
                                <li key={e}>
                                    <p>
                                        <small>{errorCopy[e]}</small>
                                    </p>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            )}
            <div
                className={`broadcast-details-form ${
                    styles[`new-livestream-form-container`]
                }`}
            >
                {exists(videoPlayerForm.playlistIds) && (
                    <PlayerSelectModal
                        isOpen={playlistSelectModalIsOpen}
                        setIsOpen={setPlaylistSelectModalIsOpen}
                        handleSelect={(players: string[]): void => {
                            dispatch(
                                updateVideoPlayerForm({ playlistIds: players })
                            );
                        }}
                        previouslySelectedIds={videoPlayerForm.playlistIds}
                        allowUnselect
                        allowNoSelection
                        emptyStateMessage={hasCatalogClaim && emptyState}
                    />
                )}
                <WebLinkSelectModal
                    links={videoPlayerForm.links}
                    isOpen={linkSelectModalIsOpen}
                    broadcastId={undefined}
                    setIsOpen={setLinkSelectModalIsOpen}
                    onSuccess={(links: BroadcastWebLink[]) => {
                        dispatch(updateVideoPlayerForm({ links }));
                    }}
                    isUpload={true}
                />
                <TextInput
                    id="name"
                    className="name"
                    label={t("video-upload:name")}
                    placeholder={t("platforms:title-placeholder")}
                    type="text"
                    value={generalForm.title}
                    onChange={(event) => {
                        dispatch(
                            updateGeneralForm({ title: event.target.value })
                        );
                    }}
                ></TextInput>
                <TextArea
                    id="description"
                    label={t("video-upload:description")}
                    placeholder={t("platforms:event-description")}
                    value={generalForm.description}
                    onChange={(event) => {
                        dispatch(
                            updateGeneralForm({
                                description: event.target.value
                            })
                        );
                    }}
                ></TextArea>
                <fieldset className={styles["schedule-fieldset"]}>
                    <ScheduleVideoVisibilityBar
                        broadcastStatus={BroadcastStatus._0}
                        setBroadcastStatus={undefined}
                        startsAt={generalForm.scheduledStartTime.toISOString()}
                        setStartsAt={(startsAt: string) => {
                            dispatch(
                                updateGeneralForm({
                                    scheduledStartTime: new Date(startsAt)
                                })
                            );
                        }}
                        liveBroadcast={undefined}
                        isUpload={true}
                        isPremiere={false}
                        isScheduledLivestream={true}
                    />
                    {hasCatalogClaim && (
                        <Toggle
                            on={generalForm.showInCatalog}
                            onToggle={() =>
                                dispatch(
                                    updateGeneralForm({
                                        showInCatalog:
                                            !generalForm.showInCatalog
                                    })
                                )
                            }
                            label={t("video-upload:show-in-catalog")}
                            reverseLayout
                            disabled={false}
                            className={styles["small-label"]}
                        />
                    )}
                </fieldset>
                <fieldset>
                    <h6>{t("video-upload:quality-select")}</h6>
                    <StreamQualitySelect
                        streamQuality={generalForm?.selectedQuality}
                        setStreamQuality={(quality: QualitySelection) =>
                            dispatch(
                                updateGeneralForm({ selectedQuality: quality })
                            )
                        }
                    />
                    <>{SpeedTestDisplay}</>
                </fieldset>
                <fieldset>
                    <h6>{t("video-upload:thumbnail")}</h6>
                    <ThumbnailUploader
                        setThumbnailFile={(file: File | null) =>
                            dispatch(
                                updateGeneralForm({
                                    scheduledImageFile: file,
                                    hasCustomThumbnail: !!file
                                })
                            )
                        }
                        thumbnailImageURL={generalForm?.scheduledImageFileUrl}
                        setThumbnailImageURL={(url: string) =>
                            dispatch(
                                updateGeneralForm({
                                    scheduledImageFileUrl: url
                                })
                            )
                        }
                        onRemove={() => {
                            dispatch(
                                updateGeneralForm({
                                    scheduledImageFile: null,
                                    scheduledImageFileUrl: "",
                                    hasCustomThumbnail: false
                                })
                            );
                        }}
                    />
                </fieldset>
                <Toggle
                    on={generalForm?.showPremiereCountdown}
                    onToggle={() => {
                        dispatch(
                            updateGeneralForm({
                                showPremiereCountdown:
                                    !generalForm?.showPremiereCountdown
                            })
                        );
                    }}
                    label={t("video-upload:show-countdown")}
                    reverseLayout
                />
                {exists(videoPlayerForm?.playlistIds) && (
                    <DisplaySelectedButton
                        selected={videoPlayerForm.playlistIds}
                        onClick={() => setPlaylistSelectModalIsOpen(true)}
                        label={
                            hasCatalogClaim
                                ? t("video-upload:collections")
                                : t("video-upload:players")
                        }
                        buttonText={
                            hasCatalogClaim
                                ? t("video-upload:add-to-collections")
                                : t("video-upload:add-to-playlists")
                        }
                        //Singular key here for dynamic pluralization of text
                        itemText={
                            hasCatalogClaim
                                ? t("video-upload:collection")
                                : t("video-upload:player")
                        }
                        variant="new-block"
                    />
                )}
                <CategoriesAssignment
                    categoryType={CategoryType._0}
                    selectedCategories={generalForm?.selectedCategories}
                    setSelectedCategories={(categories: Category[]) => {
                        dispatch(
                            updateGeneralForm({
                                selectedCategories: categories
                            })
                        );
                    }}
                />
                {exists(videoPlayerForm.links) && (
                    <DisplaySelectedButton
                        selected={videoPlayerForm.links}
                        onClick={() => setLinkSelectModalIsOpen(true)}
                        label={t("video-upload:links")}
                        buttonText={t("video-upload:add-links")}
                        //Singular key here for dynamic pluralization of text
                        itemText={t("video-upload:link")}
                        variant="new-block"
                    />
                )}
                <BroadcastDetailsGatingOptions
                    headingText={t("shared:gating-options")}
                    isPasswordGatingEnabled={
                        videoPlayerForm?.isPasswordGatingEnabled
                    }
                    setIsPasswordGatingEnabled={(enabled: boolean) => {
                        dispatch(
                            updateVideoPlayerForm({
                                isPasswordGatingEnabled: enabled
                            })
                        );
                    }}
                    password={videoPlayerForm?.password}
                    handlePasswordChange={(password: string) => {
                        dispatch(updateVideoPlayerForm({ password }));
                    }}
                    isEmailGatingEnabled={videoPlayerForm?.isEmailGatingEnabled}
                    handleEmailToggleChange={(enabled: boolean) => {
                        dispatch(
                            updateVideoPlayerForm({
                                isEmailGatingEnabled: enabled
                            })
                        );
                    }}
                    pricingSelectModalOpen={pricingSelectModalOpen}
                    setPricingSelectModalOpen={setPricingSelectModalOpen}
                    creatorProductId={videoPlayerForm?.creatorProductId}
                    handleSetCreatorProductId={(creatorProductId: string) => {
                        dispatch(updateVideoPlayerForm({ creatorProductId }));
                    }}
                    removeHeadingMargin={true}
                />
                {storeCredentialsLoading &&
                    shopifyStoreCredentials?.length > 0 && (
                        <Toggle
                            on={videoPlayerForm.enableLiveShopping}
                            onToggle={() => {
                                dispatch(
                                    updateVideoPlayerForm({
                                        enableLiveShopping:
                                            !videoPlayerForm.enableLiveShopping
                                    })
                                );
                            }}
                            label={t("video-upload:enable-live-shopping")}
                            reverseLayout
                        />
                    )}

                {exists(externalDestinations) &&
                    externalDestinations.length > 0 && (
                        <div>
                            <h6>{t("video-upload:destinations")}</h6>
                            <DestinationDisplay
                                destinations={externalDestinations}
                            />
                        </div>
                    )}
            </div>
        </>
    );
};
